import React, { useRef, useState } from 'react';
import { Download, Upload as UploadIcon, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Esporta/importa l'intero contenuto del sito (DB SQLite) come file JSON.
 * Utile per:
 * - inviarlo all'agente per replicare il preview
 * - eseguire backup periodici
 * - migrare i contenuti tra ambienti (es. live ↔ test)
 */
export default function BackupTools() {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const apiBase = (typeof window !== 'undefined') ? `${window.location.origin}/api/index.php` : '/api/index.php';

  const exportBackup = async () => {
    setBusy(true);
    try {
      const raw = localStorage.getItem('adminAuth');
      const token = raw ? (JSON.parse(raw).token || '') : '';
      const res = await fetch(`${apiBase}?route=backup&_=${Date.now()}`, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `memorial-backup-${ts}.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      toast.success('Backup scaricato');
    } catch (e) {
      toast.error(e?.message || 'Errore export');
    }
    setBusy(false);
  };

  const importBackup = async (file) => {
    if (!file) return;
    if (!window.confirm("ATTENZIONE: l'import sostituirà completamente i contenuti esistenti (squadre, calendario, sponsor, pagine, brochure, ecc.). Procedere?")) return;
    setBusy(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const raw = localStorage.getItem('adminAuth');
      const token = raw ? (JSON.parse(raw).token || '') : '';
      const res = await fetch(`${apiBase}?route=backup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
        body: JSON.stringify(data),
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out?.error || `HTTP ${res.status}`);
      toast.success(`Backup importato (${out.imported_tables?.length || 0} sezioni)`);
    } catch (e) {
      toast.error(e?.message || 'Errore import');
    }
    setBusy(false);
  };

  return (
    <div className="space-y-3" data-testid="backup-tools">
      <p className="text-white/70 text-sm">
        Scarica un file JSON con TUTTI i contenuti del sito (squadre, calendario, classifiche,
        regolamento, sponsor, pagine, brochure, edizioni, gallery, video, campi, impostazioni).
        Inviamelo per allineare il preview al sito online.
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={exportBackup}
          disabled={busy}
          className="btn-primary !py-2 !px-4 !text-sm"
          data-testid="backup-export-button"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          Esporta backup (.json)
        </button>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="px-4 py-2 rounded-full bg-navy-dark border border-white/15 text-white/80 hover:text-pink-brand hover:border-pink-brand text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2"
          data-testid="backup-import-button"
        >
          <UploadIcon size={14} /> Importa backup (.json)
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) importBackup(f); e.target.value = ''; }}
        />
      </div>
      <div className="flex items-start gap-2 text-amber-300/90 text-xs bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
        <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
        <div>
          <strong>Import:</strong> sostituisce TUTTI i dati esistenti. Le immagini e i file (cartella <code>api/uploads/</code>) NON sono inclusi nel JSON: vanno copiati manualmente via FTP se cambi server.
        </div>
      </div>
    </div>
  );
}
